import React from 'react';
import {
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaArrowsAltV,
  FaQuestion
} from 'react-icons/fa';

export const TrendIcon = ({ trend, size = 'md' }) => {
  if (!trend) {
    return (
      <div className={`flex items-center justify-center ${getSizeClasses(size)}`}>
        <span className="text-gray-400">-</span>
      </div>
    );
  }

  const { icon, color, bgColor, description } = getTrendConfig(trend);
  const sizeClasses = getSizeClasses(size);

  return (
    <div className={`flex items-center justify-center ${sizeClasses}`} title={description}>
      <div className={`rounded-full ${bgColor} flex items-center justify-center ${sizeClasses}`}>
        <span className={`${color} font-bold`}>{icon}</span>
      </div>
    </div>
  );
};

const getTrendConfig = (trend) => {
  const trendLower = String(trend).toLowerCase();
  
  switch (trendLower) {
    case 'positive':
    case 'up':
    case 'increasing':
    case 'improved':
      return {
        icon: <FaArrowUp />,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        description: 'Positive trend - sentiment improved during call'
      };
    
    case 'negative':
    case 'down':
    case 'decreasing':
    case 'declined':
      return {
        icon: <FaArrowDown />,
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        description: 'Negative trend - sentiment declined during call'
      };
    
    case 'stable':
    case 'neutral':
    case 'unchanged':
    case 'flat':
      return {
        icon: <FaArrowRight />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        description: 'Stable trend - sentiment remained consistent'
      };
    
    case 'volatile':
    case 'mixed':
    case 'variable':
      return {
        icon: <FaArrowsAltV />,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        description: 'Mixed trend - sentiment varied during call'
      };
    
    default:
      return {
        icon: <FaQuestion />,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        description: 'Unknown trend'
      };
  }
};

const getSizeClasses = (size) => {
  switch (size) {
    case 'sm':
      return 'w-6 h-6 text-sm';
    case 'lg':
      return 'w-10 h-10 text-xl';
    case 'xl':
      return 'w-12 h-12 text-2xl';
    default:
      return 'w-8 h-8 text-base';
  }
};

// Text-based trend indicator
export const TrendText = ({ trend, showIcon = true }) => {
  if (!trend) {
    return <span className="text-gray-500">No change</span>;
  }

  const { text, color, icon } = getTrendTextConfig(trend);

  return (
    <span className={color}>
      {showIcon && `${icon} `}{text}
    </span>
  );
};

const getTrendTextConfig = (trend) => {
  const trendLower = String(trend).toLowerCase();
  
  switch (trendLower) {
    case 'positive':
    case 'up':
    case 'increasing':
    case 'improved':
      return {
        text: 'Improving',
        color: 'text-green-600',
        icon: <FaArrowUp />
      };
    
    case 'negative':
    case 'down':
    case 'decreasing':
    case 'declined':
      return {
        text: 'Declining',
        color: 'text-red-600',
        icon: <FaArrowDown />
      };
    
    case 'stable':
    case 'neutral':
    case 'unchanged':
    case 'flat':
      return {
        text: 'Stable',
        color: 'text-gray-600',
        icon: <FaArrowRight />
      };
    
    case 'volatile':
    case 'mixed':
    case 'variable':
      return {
        text: 'Variable',
        color: 'text-yellow-600',
        icon: <FaArrowsAltV />
      };
    
    default:
      return {
        text: 'Unknown',
        color: 'text-gray-500',
        icon: <FaQuestion />
      };
  }
};

// Trend arrow component for numeric values
export const TrendArrow = ({ value, previousValue, showPercentage = true }) => {
  if (value === null || value === undefined || previousValue === null || previousValue === undefined) {
    return null;
  }

  const change = value - previousValue;
  const percentChange = previousValue !== 0 ? (change / previousValue) * 100 : 0;
  
  if (Math.abs(percentChange) < 0.1) {
    return (
      <span className="text-gray-500 text-sm">
        <FaArrowRight className="inline" /> {showPercentage && '0.0%'}
      </span>
    );
  }

  const isPositive = change > 0;
  const color = isPositive ? 'text-green-600' : 'text-red-600';
  const arrow = isPositive ? <FaArrowUp className="inline" /> : <FaArrowDown className="inline" />;

  return (
    <span className={`${color} text-sm font-medium`}>
      {arrow} {showPercentage && `${Math.abs(percentChange).toFixed(1)}%`}
    </span>
  );
};

export default TrendIcon;